import { ReactElement, useCallback } from 'react';
import { AxiosResultDefaultError } from '../../../../api/request';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import { getHistory } from '../api';
import { StudentsRemovalRequestHistoryResponse } from '../types/history';

export interface StudentsRemovalRequestHistoryQuery {
  page: number;
  studentNameOrId?: string;
  studyPlanVersionId?: number;
  requestState?: string;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  StudentsRemovalRequestHistoryQuery,
  StudentsRemovalRequestHistoryResponse,
  AxiosResultDefaultError
>();

export const useStudentsRemovalRequestHistoryList = useFetch;

export function StudentsRemovalRequestHistoryListConsumer({
  children,
}: {
  children: (args: {
    data?: StudentsRemovalRequestHistoryResponse;
    loading: boolean;
    refresh: () => Promise<void>;
  }) => ReactElement;
}) {
  return (
    <FetchConsumer>
      {({ data, error, loading, refresh }) => {
        if (error) {
          return (
            <DisplayError
              insideCard
              textBody={error.code}
              retryAction={refresh}
              loadingAction={loading}
            />
          );
        }

        return children({ data, loading, refresh });
      }}
    </FetchConsumer>
  );
}

export function StudentsRemovalRequestHistoryListLoader({
  children,
}: {
  children: ReactElement | ReactElement[];
}) {
  const request = useCallback(
    async (query: StudentsRemovalRequestHistoryQuery) => {
      return getHistory({
        ...query,
        studentNameOrId: query.studentNameOrId || undefined,
        page: query.page - 1,
      });
    },
    [],
  );

  return (
    <FetchProvider
      request={request}
      defaultQuery={{ page: 1 }}
      children={children}
      fetchImmediately
    />
  );
}
